/**
 * The channel selection, shared by every page that shows it.
 *
 * WHY A CONTEXT AND NOT PER-PAGE STATE, while the period selector beside it IS
 * per-page state. The owner asked for the channel choice to survive navigation
 * "เพื่อกันความสับสน" — and the confusion is real and specific: tick Facebook
 * only on the dashboard, click through to Media Intelligence, and a per-page
 * filter silently shows you every channel again while the control still sits
 * there looking like it means something. The period does not have that problem
 * because each page labels its own range in the figures it prints.
 *
 * PERSISTED TO localStorage, following `ydm.dashboard.advisorEnabled` — the one
 * other per-viewer preference here: same `ydm.*` key shape, same try/catch on BOTH
 * the read and the write, same rule that a browser refusing storage must cost the
 * reader the memory and nothing else. The selection is a view preference, never
 * data: nothing here is sent anywhere, and a stale value can only ever narrow
 * what one person sees on their own screen.
 *
 * @module contexts/ChannelFilterContext
 */

import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { AD_CHANNEL_IDS, isFiltering, type ChannelSelection } from "@/services/channelFilter";

const STORAGE_KEY = "ydm.filters.channels";

interface ChannelFilterValue {
  /** Ticked channel ids. EMPTY MEANS ALL — see `isChannelShown`. */
  selected: ChannelSelection;
  /** True only for a real subset, so a badge never claims a filter that is not on. */
  filtering: boolean;
  toggle: (channelId: string) => void;
  /** Back to every channel, stored as the empty set rather than all four. */
  clear: () => void;
}

const ChannelFilterContext = createContext<ChannelFilterValue | null>(null);

/**
 * Read the remembered selection.
 *
 * Every failure mode lands on "no filter": absent key, malformed JSON, a non-array,
 * or an id that is no longer a channel. The last one matters — a stored
 * `["analytics"]` from a build where GA was offered would otherwise filter the
 * page down to a channel the vocabulary no longer governs, which `isChannelShown`
 * passes through, leaving a selection that hides everything it does govern.
 */
const readStored = (): Set<string> => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return new Set();
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set();
    const known = (AD_CHANNEL_IDS as readonly string[]).filter((id) => parsed.includes(id));
    // All four stored is the same view as none; normalise so `filtering` cannot
    // report a filter that shows everything.
    return known.length === AD_CHANNEL_IDS.length ? new Set() : new Set(known);
  } catch {
    return new Set();
  }
};

const write = (next: Set<string>): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify([...next]));
  } catch {
    // A browser refusing storage costs the reader the memory, not the filter.
  }
};

export const ChannelFilterProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [selected, setSelected] = useState<Set<string>>(readStored);

  const toggle = useCallback((channelId: string) => {
    setSelected((current) => {
      // A new Set every time: mutating state in place is the thing this codebase
      // bans outright, and React would not re-render on it anyway.
      const next = new Set(current);
      if (next.has(channelId)) next.delete(channelId);
      else next.add(channelId);
      // Unticking the last box, or ticking all four, both mean "show everything".
      // Stored as the empty set so the two cannot drift apart.
      const normalised = next.size === 0 || next.size === AD_CHANNEL_IDS.length ? new Set<string>() : next;
      write(normalised);
      return normalised;
    });
  }, []);

  const clear = useCallback(() => {
    const empty = new Set<string>();
    write(empty);
    setSelected(empty);
  }, []);

  const value = useMemo<ChannelFilterValue>(
    () => ({ selected, filtering: isFiltering(selected), toggle, clear }),
    [selected, toggle, clear],
  );

  return <ChannelFilterContext.Provider value={value}>{children}</ChannelFilterContext.Provider>;
};

/**
 * The selection, or an inert "everything" when no provider is mounted.
 *
 * Deliberately does NOT throw. A page rendered in a test, or any tree that forgets
 * the provider, gets an unfiltered view — which is the view it had before this
 * existed. Throwing would turn a missing wrapper into a blank screen on pages
 * whose numbers have nothing to do with channels.
 */
export const useChannelFilter = (): ChannelFilterValue => {
  const ctx = useContext(ChannelFilterContext);
  return (
    ctx ?? {
      selected: new Set<string>(),
      filtering: false,
      toggle: () => {},
      clear: () => {},
    }
  );
};
